// Tiny wrapper around `xlsx` that produces a download with consistent
// formatting (bold headers + auto-fit column widths) for the dashboard's
// "Export" buttons. Designed to round-trip the corresponding "Import":
// callers pass an array of plain objects with the same column names the
// importer expects, plus an optional explicit column order.

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const todayStamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Width = max length seen in column (capped) + a small padding. Excel widths
// are character units, not pixels, so this is approximate but gives a clean
// "no truncated content" feel without going overboard on long URLs.
const computeColumnWidths = (rows, headers) => {
  const MIN = 12;
  const MAX = 60;
  return headers.map((h) => {
    let max = String(h).length;
    for (const row of rows) {
      const v = row?.[h];
      if (v == null) continue;
      const s = typeof v === 'string' ? v : (typeof v === 'object' ? JSON.stringify(v) : String(v));
      if (s.length > max) max = s.length;
      if (max >= MAX) break;
    }
    return { wch: Math.min(MAX, Math.max(MIN, max + 2)) };
  });
};

// Normalize cell values for spreadsheet output: arrays + objects become JSON,
// booleans become TRUE/FALSE, null/undefined become ''. Numbers stay numeric
// so Excel renders them right-aligned.
const normalizeCell = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const exportRowsToXlsx = async ({ rows, columns, filename, sheetName = 'Sheet1' }) => {
  const XLSX = await import('xlsx');
  const headers = columns && columns.length ? columns : Object.keys(rows[0] || {});
  const normalized = rows.map((row) => {
    const out = {};
    for (const h of headers) out[h] = normalizeCell(row?.[h]);
    return out;
  });

  const sheet = XLSX.utils.json_to_sheet(normalized, { header: headers });

  // Bold the header row. SheetJS keeps cell styles only when written via the
  // pro fork; the community build silently drops `s`. We still set it so the
  // file looks polished anywhere a style-aware reader opens it.
  for (let c = 0; c < headers.length; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (sheet[addr]) {
      sheet[addr].s = {
        font: { bold: true },
        alignment: { vertical: 'center' },
      };
    }
  }

  sheet['!cols'] = computeColumnWidths(normalized, headers);
  // Freeze the header row so it stays visible when scrolling long sheets.
  sheet['!freeze'] = { xSplit: 0, ySplit: 1 };

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, sheetName);
  const buffer = XLSX.write(book, { type: 'array', bookType: 'xlsx' });
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const stamped = filename.includes('{date}')
    ? filename.replace('{date}', todayStamp())
    : `${filename}-${todayStamp()}.xlsx`;
  downloadBlob(blob, stamped.endsWith('.xlsx') ? stamped : `${stamped}.xlsx`);
};

export default exportRowsToXlsx;
